import {DocumentTitleList} from './DocumentTitleList';
import {FormEvent, useLayoutEffect, useId, useRef, useState} from 'react';
import './CustomerServiceRequestIntake.css';
import {attorneyIntakeFields, AttorneyIntakeFieldName, AttorneyIntakeFields, AttorneyIntakeSelectOption} from './AttorneyIntakeFields';
import {carolinaWorkflowStore, documentHandlingFee, PAGE_COUNT_FREE_ALLOWANCE} from './carolinaWorkflowStore';
import {friendlyMutationError} from './friendlyMutationError';
import {HolmesJrGuide} from './HolmesJrGuide';
import {WalkthroughNavigate} from './walkthroughViews';
import {ApiError, DraftDetails, uploadOrderDocument} from './api';
import {CompanyDocumentIntake, ReviewedIntake} from './CompanyDocumentIntake';

/**
 * Field vocabulary and step names deliberately match PR #112's
 * CustomerServiceRequestPreview.tsx (Matter/Parties/Recipient/Documents/
 * Instructions/Review) so this journey reads as the same product once the
 * two are reconciled, per Issue #114's "align with PR #112, reuse rather
 * than duplicate" directive. This module skips the account/login/MFA/
 * organization-setup screens PR #112 has upstream of this (out of scope --
 * auth is explicitly not something Claude modifies) and goes straight to
 * the request itself, assuming the customer is already signed in.
 *
 * Deliberately NOT reused: CustomerDocumentUploadAdapter and
 * CustomerServiceDraftAdapter (PR #112) -- those simulate a real async
 * upload/draft-versioning backend and are substantial, already-tested
 * pieces on their own branch. Forking them here would be exactly the kind
 * of duplicate implementation Issue #114 warns against. This module uses a
 * plain, honest placeholder for documents instead (see the Documents step)
 * and should be swapped for the real adapter once PR #112 lands.
 */

type Step = 'Matter' | 'Parties' | 'Recipient' | 'Documents' | 'Instructions' | 'Review';
const STEPS: Step[] = ['Matter', 'Parties', 'Recipient', 'Documents', 'Instructions', 'Review'];

const matterFields: readonly AttorneyIntakeFieldName[] = ['client_reference', 'case_caption', 'court_reference', 'case_number', 'service_type_code', 'deadline_date'];
const recipientFields: readonly AttorneyIntakeFieldName[] = ['recipient_name', 'recipient_type_code', 'address_line_1', 'address_line_2', 'city', 'admin_area', 'postal_code', 'country_code'];
const customerFieldLabels: Partial<Record<AttorneyIntakeFieldName, string>> = {service_type_code: 'Service type', recipient_type_code: 'Recipient type'};
const customerSelectOptions: Partial<Record<AttorneyIntakeFieldName, readonly AttorneyIntakeSelectOption[]>> = {
  service_type_code: [
    {value: 'standard_process', label: 'Standard process service'},
    {value: 'subpoena', label: 'Subpoena'},
    {value: 'notice', label: 'Notice'},
  ],
  recipient_type_code: [
    {value: 'individual', label: 'Individual'},
    {value: 'business', label: 'Business or organization'},
    {value: 'government', label: 'Government entity'},
  ],
};
const requiredByStep: Partial<Record<Step, readonly string[]>> = {
  Matter: ['requesting_organization', 'case_caption', 'service_type_code', 'deadline_date'],
  Parties: ['plaintiff', 'defendant', 'contact_name', 'contact_email'],
  Recipient: ['recipient_name', 'recipient_type_code', 'address_line_1', 'city', 'admin_area', 'postal_code', 'country_code'],
  Instructions: ['attempt_notes'],
};
const documentKindOptions = ['Summons and complaint', 'Subpoena', 'Notice', 'Other'];

const STEP_TIP: Record<Step, string> = {
  Matter: "Tell us about the matter — your organization, the case, and when it needs to be served.",
  Parties: 'Who is suing whom, and who should we reach if we have a question about this request?',
  Recipient: 'Who needs to be served, and where can they be found?',
  Documents: "Let us know what you'll be sending — the real upload happens in the next step here once it's connected.",
  Instructions: "Anything the server should know before attempting service? Access notes are optional but helpful.",
  Review: 'Check everything below, then choose how it should be routed and submit.',
};

type FieldValues = Record<string, string>;

const initialValues: FieldValues = {
  requesting_organization: '',
  client_reference: '',
  case_caption: '',
  court_reference: '',
  case_number: '',
  service_type_code: '',
  deadline_date: '',
  plaintiff: '',
  defendant: '',
  contact_name: '',
  contact_email: '',
  recipient_name: '',
  recipient_type_code: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  admin_area: '',
  postal_code: '',
  country_code: 'US',
  attempt_notes: '',
  access_notes: '',
};

const contactEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The two acceptable intake patterns discovered while mapping this UI to the
 * real backend (docs/ai/CLAUDE_UI_BACKEND_SEAM_MAP.md): "platform-routed"
 * matches the real system's intake -> custody -> routing sequence
 * (api.ts's loadIntake/claimMissionControlCustody/createFulfillmentRoute)
 * and is the recommended default, since it's the architecture the platform
 * is already built around and scales to more than one servicing company.
 * "Direct-to-company" (createCompanyFile) skips that holding stage --
 * defensible only for a single-company pilot, kept here so both patterns
 * stay real and comparable rather than one being a paper description.
 */
type RoutingMode = 'platform' | 'direct';

export type DraftConnection = {
  orderId?: string;
  onDocumentAttached?: () => void;
  actionBusy?: boolean;
  onBeginAction?: () => boolean;
  onEndAction?: () => void;
  values: Partial<DraftDetails>;
  onSave: (values: Record<string, string>) => Promise<void>;
  onReload: () => void;
  onEdit: () => void;
};

export function CustomerServiceRequestIntake({onNavigate, customerName = '', draftConnection}: {onNavigate?: WalkthroughNavigate; customerName?: string; draftConnection?: DraftConnection} = {}) {
  const steps: Step[] = draftConnection ? ['Matter', 'Recipient', 'Documents', 'Review'] : STEPS;
  const [draftBlocked, setDraftBlocked] = useState(false);
  const [draftStatus, setDraftStatus] = useState('');
  const [paperFocusRequest, setPaperFocusRequest] = useState(0);
  const [attachingPapers, setAttachingPapers] = useState(false);
  const attachingRef = useRef(false);
  const [step, setStep] = useState<Step>('Matter');
  const [values, setValues] = useState<FieldValues>(() => ({...initialValues, requesting_organization: customerName || initialValues.requesting_organization, ...Object.fromEntries(Object.entries(draftConnection?.values ?? {}).map(([key, value]) => [key, String(value ?? '')]))}));
  const [rush, setRush] = useState(false);
  const [documentKinds, setDocumentKinds] = useState<string[]>(['Summons and complaint']);
  const [additionalDocumentNote, setAdditionalDocumentNote] = useState('');
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);
  const [routingMode, setRoutingMode] = useState<RoutingMode>('platform');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [createdFileId, setCreatedFileId] = useState<string | null>(null);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A ref alongside the state: state drives the disabled/label UI, but state updates from a native
  // click handler are batched and don't update this closure until React re-renders -- a ref reads
  // and writes synchronously, so it's what actually blocks a zero-delay repeated submit.
  const isSubmittingRef = useRef(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const paperFailureRef = useRef<HTMLParagraphElement>(null);
  const focusPaperFailureRef = useRef(false);
  const appliedReadingFocusRef = useRef<string|null>(null);
  const reviewConfirmId = useId();
  const rushId = useId();

  useLayoutEffect(() => {
    // Focus before the user can type; a delayed frame can steal the first field's input.
    (createdFileId || createdRequestId ? confirmationHeadingRef.current : stepHeadingRef.current)?.focus();
  }, [step, createdFileId, createdRequestId]);

  useLayoutEffect(()=>{
    const id=appliedReadingFocusRef.current;if(!id)return;
    const target=document.getElementById(id);if(!target)return;
    appliedReadingFocusRef.current=null;
    target.scrollIntoView?.({block:'center'});
    target.focus();
  },[step,draftStatus,paperFocusRequest]);

  useLayoutEffect(()=>{
    if(!submitError||!focusPaperFailureRef.current)return;
    focusPaperFailureRef.current=false;
    paperFailureRef.current?.scrollIntoView?.({block:'center'});
    paperFailureRef.current?.focus();
  },[submitError]);

  function continueManually(){
    const target=['case_caption','court_reference','case_number'].find(name=>!values[name]?.trim())??'case_caption';
    appliedReadingFocusRef.current=`csri-${target}`;
    setSubmitError('');
    setStep('Matter');
    setDraftStatus('Continue manually below. Your selected PDF and existing form entries are preserved; nothing was submitted.');
    setPaperFocusRequest(current=>current+1);
  }

  function update(name: string, value: string) {
    if (draftConnection) {setDraftStatus(''); setReviewConfirmed(false); draftConnection.onEdit();}
    setValues((current) => ({...current, [name]: value}));
    setErrors((current) => {
      const next = {...current};
      delete next[name];
      return next;
    });
  }

  async function applyReading(reading: ReviewedIntake, selectedFiles: File[] | File) {
    const files=Array.isArray(selectedFiles)?selectedFiles:[selectedFiles];
    if (!draftConnection?.orderId || files.length===0 || attachingRef.current || isSubmittingRef.current || draftBlocked || draftConnection.actionBusy) return;
    if (draftConnection.onBeginAction?.() === false) return;
    attachingRef.current = true; setAttachingPapers(true); setSubmitError('');
    try {
      for (const file of files) {
        const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()))).map(b=>b.toString(16).padStart(2,'0')).join('');
        // Each selected source and draft reuse the same upload key after an ambiguous result; never blind-retry.
        const receipt = await uploadOrderDocument(draftConnection.orderId, file, `booking-reader-${hash}`);
        // Do not apply suggestions or claim the selected batch is stored without every scoped receipt.
        if (receipt.order_id !== draftConnection.orderId || typeof receipt.document_id !== 'string' || receipt.document_id.length !== 36
          || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.document_id)) {
          throw new Error('Paper attachment receipt could not be confirmed.');
        }
      }
      const changes: FieldValues = {};
      if (reading.fields.caseCaption !== undefined) changes.case_caption = reading.fields.caseCaption;
      if (reading.fields.courtReference !== undefined) changes.court_reference = reading.fields.courtReference;
      if (reading.fields.caseNumber !== undefined) changes.case_number = reading.fields.caseNumber;
      if (reading.documentsToServe !== undefined) changes.documents_to_serve = reading.documentsToServe.join('\n');
      if (reading.recipients.length === 1) changes.recipient_name = reading.recipients[0].subject;
      const appliedLabels:Record<string,string>={
        case_caption:'Case caption',court_reference:'Court jurisdiction',case_number:'Case number',documents_to_serve:'Documents to serve',recipient_name:'Recipient name',
      };
      const changedNames=Object.keys(changes) as (keyof FieldValues)[];
      const firstMatterField=changedNames.find(name=>['case_caption','court_reference','case_number'].includes(name));
      const targetField=firstMatterField??changedNames[0];
      if(targetField){
        const targetStep:Step=targetField==='recipient_name'?'Recipient':targetField==='documents_to_serve'?'Documents':'Matter';
        appliedReadingFocusRef.current=`csri-${targetField}`;
        setStep(targetStep);
      }
      setValues(current=>({...current,...changes})); setReviewConfirmed(false); draftConnection.onEdit();
      draftConnection.onDocumentAttached?.();
      setDraftStatus(`Applied to the editable booking fields: ${changedNames.map(name=>appliedLabels[name]).join(', ')||'no suggested fields selected'}. ${files.length} selected ${files.length===1?'document':'documents'} stored privately; document validation remains pending. Check the remaining fields, then save your draft.`);
    } catch {
      focusPaperFailureRef.current=true;
      setSubmitError('Paper attachment could not be confirmed. No suggestions were applied and nothing was submitted. Your selected PDFs and form entries are preserved. Reload the document list before trying again; no automatic retry was made.');
    } finally {attachingRef.current=false;setAttachingPapers(false);draftConnection.onEndAction?.();}
  }

  function toggleDocumentKind(kind: string) {
    setDocumentKinds((current) => (current.includes(kind) ? current.filter((item) => item !== kind) : [...current, kind]));
  }

  function validateStep(current: Step): Record<string, string> {
    const required = (requiredByStep[current] ?? []).filter(name => !draftConnection || name !== 'requesting_organization');
    const nextErrors: Record<string, string> = {};
    for (const name of required) {
      if (!values[name]?.trim()) nextErrors[name] = 'Required.';
    }
    if (current === 'Parties' && values.contact_email && !contactEmailPattern.test(values.contact_email)) {
      nextErrors.contact_email = 'Enter a valid email address.';
    }
    return nextErrors;
  }

  /**
   * Ported from Codex's PR #112 pattern (CustomerServiceRequestPreview.tsx's
   * RequestField helper): move focus to the first invalid field instead of
   * leaving it on the button the user just clicked, so a keyboard/
   * screen-reader user lands directly on what needs fixing.
   */
  function focusFirstInvalidField() {
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    });
  }

  function goNext() {
    const stepErrors = validateStep(step);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      focusFirstInvalidField();
      return;
    }
    const index = steps.indexOf(step);
    if (index < steps.length - 1) setStep(steps[index + 1]);
  }

  function goBack() {
    const index = steps.indexOf(step);
    if (index > 0) setStep(steps[index - 1]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmittingRef.current || attachingRef.current || draftConnection?.actionBusy) return;
    if (!reviewConfirmed) {
      setErrors({review_confirmed: draftConnection ? 'Confirm the details above are correct before saving.' : 'Confirm the details above are correct before submitting.'});
      focusFirstInvalidField();
      return;
    }
    if (draftConnection && draftBlocked) return;
    if (draftConnection?.onBeginAction?.() === false) return;
    setDraftStatus('');
    setSubmitError('');
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (draftConnection) {
        const fields = Object.fromEntries(attorneyIntakeFields.map(({name}) => [name, values[name] ?? '']));
        await draftConnection.onSave(fields);
        setDraftStatus('Draft saved — not submitted to ProFenz. Complete the existing attorney review step when ready. No routing, dispatch or charge occurred.');
        return;
      }
      const addressLine2 = values.address_line_2 ? `, ${values.address_line_2}` : '';
      const recipientTypeLabel = customerSelectOptions.recipient_type_code?.find((option) => option.value === values.recipient_type_code)?.label ?? values.recipient_type_code;
      const requestInput = {
        caption: values.case_caption,
        caseNumber: values.case_number,
        court: values.court_reference || 'Court to be confirmed at intake review',
        stateCode: values.admin_area || 'OTHER',
        originatingCustomer: `${values.requesting_organization} — customer-submitted`,
        ...(values.client_reference.trim() ? {sourceOrderReference: `Client ref ${values.client_reference.trim()}`} : {}),
        jobs: [
          {
            subject: `${values.recipient_name} (${recipientTypeLabel})`,
            serviceAddress: `${values.address_line_1}${addressLine2}, ${values.city}, ${values.admin_area} ${values.postal_code}`,
            serviceLevel: rush ? ('Rush' as const) : ('Standard' as const),
            deadlineDate: values.deadline_date,
            server: 'Unassigned',
            pageCount,
          },
        ],
      };
      if (routingMode === 'platform') {
        const requestId = await carolinaWorkflowStore.createPlatformIntakeRequest(requestInput, crypto.randomUUID());
        setCreatedRequestId(requestId);
      } else {
        const fileId = await carolinaWorkflowStore.createCompanyFile(requestInput, crypto.randomUUID());
        setCreatedFileId(fileId);
      }
    } catch (error) {
      if (draftConnection) {
        if (error instanceof ApiError && error.status === 422) {
          setErrors(error.errors);
          const invalidFields = Object.keys(error.errors);
          setStep(invalidFields.some(name => matterFields.includes(name as AttorneyIntakeFieldName)) ? 'Matter'
            : invalidFields.some(name => recipientFields.includes(name as AttorneyIntakeFieldName)) ? 'Recipient'
            : invalidFields.includes('documents_to_serve') ? 'Documents' : 'Recipient');
          setSubmitError('Correct the marked fields and save again.');
          focusFirstInvalidField();
        } else {
          setDraftBlocked(true);
          setSubmitError(error instanceof ApiError && error.status === 403
            ? 'Draft access was denied. Reload your authorized draft.'
            : error instanceof ApiError && error.status === 409
              ? 'The draft changed elsewhere. Reload before saving again.'
              : 'The save result could not be confirmed. Reload the draft before another save.');
        }
      } else setSubmitError(friendlyMutationError(error, 'The request could not be submitted. Try again.'));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      draftConnection?.onEndAction?.();
    }
  }

  if (createdFileId || createdRequestId) {
    return (
      <section aria-label="Customer service request confirmation" className="csri-root">
        <HolmesJrGuide
          pose="guide"
          tip={
            createdRequestId
              ? "Your request is in the platform queue now — it'll be routed to a servicing company shortly."
              : "Your request is with the operations team now — they'll assign a server and keep the file moving."
          }
        />
        <div className="csri-confirmation">
          <p className="csri-eyebrow">REQUEST SUBMITTED</p>
          <h1 ref={confirmationHeadingRef} tabIndex={-1}>
            Thank you — your request is in the queue.
          </h1>
          {createdRequestId ? (
            <p>
              Request <strong>{createdRequestId}</strong> is waiting to be routed to a servicing company.
            </p>
          ) : (
            <p>
              Company file <strong>{createdFileId}</strong> has been created and is now visible to the servicing company's operations team.
            </p>
          )}
          {/*
            Demo-only cross-role affordance: lets whoever is testing this walkthrough jump to the
            internal operations view of the same job to confirm it landed correctly. A real customer
            would never see or reach an internal Mission Control / routing-queue screen -- the copy
            below says so explicitly so this isn't mistaken for real customer-facing UX to preserve.
          */}
          <p className="csri-crosslink">
            {onNavigate ? (
              <button type="button" onClick={() => onNavigate(createdRequestId ? 'platform-routing' : 'mission-control')}>
                {createdRequestId ? 'Preview this from the platform routing queue (internal view — demo only) →' : 'Preview this from Mission Control (internal view — demo only) →'}
              </button>
            ) : (
              <a href={createdRequestId ? '/web/walkthrough/platform-routing-queue' : '/web/walkthrough/mission-control-file-workspace'}>
                {createdRequestId ? 'Preview this from the platform routing queue (internal view — demo only) →' : 'Preview this from Mission Control (internal view — demo only) →'}
              </a>
            )}
          </p>
        </div>
      </section>
    );
  }

  const stepIndex = steps.indexOf(step);
  const selectOptions = {...customerSelectOptions};
  if (draftConnection) {
    for (const name of ['service_type_code', 'recipient_type_code'] as const) {
      const saved = draftConnection.values[name];
      if (saved && !selectOptions[name]?.some(option => option.value === saved)) {
        selectOptions[name] = [...(selectOptions[name] ?? []), {value: saved, label: saved.replaceAll('_', ' ')}];
      }
    }
  }

  return (
    <section aria-label="Customer service request intake" className="csri-root">
      {!draftConnection && <HolmesJrGuide pose="guide" tip={STEP_TIP[step]} />}
      <p className="csri-flag" role="status">
        <strong>{draftConnection ? 'Authenticated draft' : 'Local synthetic module'}</strong>
        <span>{draftConnection ? (/^my company$/i.test(customerName.trim()) ? 'Save matter and recipient details before attorney review.' : `Organization: ${customerName}. Save matter and recipient details before attorney review.`) : 'Every field below is held in this browser only. Nothing is sent, stored, or billed.'}</span>
      </p>
      <header className="csri-header">
        <p className="csri-eyebrow">NEW SERVICE REQUEST</p>
        <h1 ref={stepHeadingRef} tabIndex={-1}>
          {step}
        </h1>
        <ol className="csri-steps" aria-label="Progress">
          {steps.map((label, index) => (
            <li key={label} className={index === stepIndex ? 'is-current' : index < stepIndex ? 'is-done' : undefined}>
              {label==='Documents'?'Review documents':label}
            </li>
          ))}
        </ol>
      </header>

      {draftStatus && <p className="csri-note" role="status">{draftStatus}</p>}
      {draftConnection && submitError && <p ref={paperFailureRef} className="csri-note" role="alert" tabIndex={-1}>{submitError}</p>}
      {draftConnection && draftBlocked && <button className="csri-secondary" type="button" onClick={draftConnection.onReload}>Reload draft</button>}
      {/*
       * Mounted continuously (never conditionally unmounted) so its own internal state --
       * selected/read files, extraction suggestions, confirmed selections -- survives moving
       * between steps. `hidden` only controls whether it's shown: the court-paper upload and
       * review panel belongs to Matter (where the initial read happens) and Documents (where
       * "Add another document" stays reachable); it must not appear above Recipient, Parties
       * or Review and force scrolling past duplicate upload/court-confirmation sections to
       * reach that step's own form (Issue #530 founder correction, live mobile walkthrough).
       */}
      <div hidden={step !== 'Matter' && step !== 'Documents'}>
        {draftConnection?.orderId && <CompanyDocumentIntake autoRead bookingOrderId={draftConnection.orderId} disabled={draftBlocked||isSubmitting||attachingPapers||draftConnection.actionBusy} onStorePapers={files=>void applyReading({fields:{},recipients:[]},files)} onContinueManually={continueManually} onReviewed={(reading,file)=>void applyReading(reading,file)}/>}
        {attachingPapers && <p role="status">Attaching all selected papers to your private booking draft…</p>}
      </div>
      <form className="csri-form" onSubmit={step === 'Review' ? submit : (event) => event.preventDefault()} noValidate>
        {step === 'Matter' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>Matter details</legend>
            {!draftConnection && <div className="csri-field">
              <label htmlFor="csri-requesting-organization" data-required>
                Your firm / organization
              </label>
              <input
                id="csri-requesting-organization"
                required
                value={values.requesting_organization}
                onChange={(event) => update('requesting_organization', event.target.value)}
                aria-invalid={errors.requesting_organization ? 'true' : undefined}
                aria-describedby={errors.requesting_organization ? 'csri-requesting-organization-error' : undefined}
              />
              {errors.requesting_organization && (
                <span id="csri-requesting-organization-error" className="csri-field-error">
                  {errors.requesting_organization}
                </span>
              )}
            </div>}
            <AttorneyIntakeFields
              names={matterFields}
              values={values}
              errors={errors}
              idPrefix="csri"
              fieldClassName="csri-field"
              labels={customerFieldLabels}
              selectOptions={selectOptions}
              onChange={update}
            />
            {!draftConnection && <label className="csri-checkbox" htmlFor={rushId}>
              <input id={rushId} type="checkbox" checked={rush} onChange={(event) => setRush(event.target.checked)} />
              This is time-sensitive and needs rush handling
            </label>}
          </fieldset>
        )}

        {step === 'Parties' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>Parties</legend>
            <div className="csri-field">
              <label htmlFor="csri-plaintiff" data-required>
                Plaintiff / petitioner
              </label>
              <input
                id="csri-plaintiff"
                required
                value={values.plaintiff}
                onChange={(event) => update('plaintiff', event.target.value)}
                aria-invalid={errors.plaintiff ? 'true' : undefined}
                aria-describedby={errors.plaintiff ? 'csri-plaintiff-error' : undefined}
              />
              {errors.plaintiff && (
                <span id="csri-plaintiff-error" className="csri-field-error">
                  {errors.plaintiff}
                </span>
              )}
            </div>
            <div className="csri-field">
              <label htmlFor="csri-defendant" data-required>
                Defendant / respondent
              </label>
              <input
                id="csri-defendant"
                required
                value={values.defendant}
                onChange={(event) => update('defendant', event.target.value)}
                aria-invalid={errors.defendant ? 'true' : undefined}
                aria-describedby={errors.defendant ? 'csri-defendant-error' : undefined}
              />
              {errors.defendant && (
                <span id="csri-defendant-error" className="csri-field-error">
                  {errors.defendant}
                </span>
              )}
            </div>
            <div className="csri-field">
              <label htmlFor="csri-contact-name" data-required>
                Your contact name
              </label>
              <input
                id="csri-contact-name"
                required
                value={values.contact_name}
                onChange={(event) => update('contact_name', event.target.value)}
                aria-invalid={errors.contact_name ? 'true' : undefined}
                aria-describedby={errors.contact_name ? 'csri-contact-name-error' : undefined}
              />
              {errors.contact_name && (
                <span id="csri-contact-name-error" className="csri-field-error">
                  {errors.contact_name}
                </span>
              )}
            </div>
            <div className="csri-field">
              <label htmlFor="csri-contact-email" data-required>
                Your contact email
              </label>
              <input
                id="csri-contact-email"
                type="email"
                required
                value={values.contact_email}
                onChange={(event) => update('contact_email', event.target.value)}
                aria-invalid={errors.contact_email ? 'true' : undefined}
                aria-describedby={errors.contact_email ? 'csri-contact-email-error' : undefined}
              />
              {errors.contact_email && (
                <span id="csri-contact-email-error" className="csri-field-error">
                  {errors.contact_email}
                </span>
              )}
            </div>
          </fieldset>
        )}

        {step === 'Recipient' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>Who needs to be served</legend>
            <AttorneyIntakeFields
              names={recipientFields}
              values={values}
              errors={errors}
              idPrefix="csri"
              fieldClassName="csri-field"
              labels={customerFieldLabels}
              selectOptions={selectOptions}
              onChange={update}
            />
          </fieldset>
        )}

        {step === 'Documents' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>Documents you'll provide</legend>
            {draftConnection?<><DocumentTitleList id="csri-documents_to_serve" value={values.documents_to_serve??''} error={errors.documents_to_serve} onChange={value=>update('documents_to_serve',value)}/><p>Review the document titles and the papers you added earlier. You do not need to upload them again. If something is missing, use Add another document in the court-papers area above.</p></>:<>
            <p className="csri-note">
              Select what you'll be sending over. This is a placeholder for the full upload experience already built and tested separately (PR #112's document
              upload adapter) — reuse that when it lands here rather than rebuilding it.
            </p>
            <div className="csri-checkbox-list">
              {documentKindOptions.map((kind) => (
                <label className="csri-checkbox" key={kind}>
                  <input type="checkbox" checked={documentKinds.includes(kind)} onChange={() => toggleDocumentKind(kind)} />
                  {kind}
                </label>
              ))}
            </div>
            <div className="csri-field">
              <label htmlFor="csri-additional-documents">Anything else we should know about the documents (optional)</label>
              <textarea id="csri-additional-documents" value={additionalDocumentNote} onChange={(event) => setAdditionalDocumentNote(event.target.value)} />
            </div>
            <div className="csri-field">
              <label htmlFor="csri-page-count">Pages in this recipient's copy (optional — first {PAGE_COUNT_FREE_ALLOWANCE} free)</label>
              <input
                id="csri-page-count"
                type="number"
                min={0}
                value={pageCount ?? ''}
                onChange={(event) => setPageCount(event.target.value ? Number(event.target.value) : undefined)}
              />
            </div>
            {typeof pageCount === 'number' && pageCount > 0 && (
              <p className="csri-note">
                {documentHandlingFee(pageCount) > 0
                  ? `Estimated document handling fee: $${documentHandlingFee(pageCount).toFixed(2)} (${pageCount - PAGE_COUNT_FREE_ALLOWANCE} page${pageCount - PAGE_COUNT_FREE_ALLOWANCE === 1 ? '' : 's'} over the free ${PAGE_COUNT_FREE_ALLOWANCE}).`
                  : `Within the free ${PAGE_COUNT_FREE_ALLOWANCE}-page allowance — no handling fee.`}
              </p>
            )}
            </>}
          </fieldset>
        )}

        {step === 'Instructions' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>Service instructions</legend>
            <div className="csri-field">
              <label htmlFor="csri-attempt-notes" data-required>
                What should the server know before attempting service?
              </label>
              <textarea
                id="csri-attempt-notes"
                required
                value={values.attempt_notes}
                onChange={(event) => update('attempt_notes', event.target.value)}
                aria-invalid={errors.attempt_notes ? 'true' : undefined}
                aria-describedby={errors.attempt_notes ? 'csri-attempt-notes-error' : undefined}
              />
              {errors.attempt_notes && (
                <span id="csri-attempt-notes-error" className="csri-field-error">
                  {errors.attempt_notes}
                </span>
              )}
            </div>
            <div className="csri-field">
              <label htmlFor="csri-access-notes">Access notes (gate codes, best times, etc. — optional)</label>
              <textarea id="csri-access-notes" value={values.access_notes} onChange={(event) => update('access_notes', event.target.value)} />
            </div>
          </fieldset>
        )}

        {step === 'Review' && (
          <fieldset className="csri-fieldset" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
            <legend>{draftConnection ? 'Review before saving draft' : 'Review before submitting'}</legend>
            <dl className="csri-review-list">
              <div>
                <dt>Matter</dt>
                <dd>
                  {values.case_caption}{values.client_reference.trim() && ` · ${values.client_reference.trim()}`} {rush && '· Rush'}
                </dd>
              </div>
              <div>
                <dt>Recipient</dt>
                <dd>
                  {values.recipient_name}, {values.address_line_1}, {values.city}, {values.admin_area} {values.postal_code}
                </dd>
              </div>
              <div>
                <dt>Deadline</dt>
                <dd>{values.deadline_date}</dd>
              </div>
              {draftConnection&&<div><dt>Documents to serve</dt><dd style={{whiteSpace:'pre-line'}}>{values.documents_to_serve||'Not recorded'}</dd></div>}
              {!draftConnection && <div>
                <dt>Documents</dt>
                <dd>{documentKinds.join(', ') || 'None selected'}</dd>
              </div>}
              {typeof pageCount === 'number' && pageCount > 0 && (
                <div>
                  <dt>Document handling fee</dt>
                  <dd>{documentHandlingFee(pageCount) > 0 ? `$${documentHandlingFee(pageCount).toFixed(2)} (${pageCount} pages)` : `None — within the free ${PAGE_COUNT_FREE_ALLOWANCE}`}</dd>
                </div>
              )}
            </dl>
            {!draftConnection && <fieldset className="csri-routing">
              <legend>How should this request reach a servicing company?</legend>
              <label className="csri-routing-option">
                <input type="radio" name="routing-mode" value="platform" aria-label="Platform-routed" checked={routingMode === 'platform'} onChange={() => setRoutingMode('platform')} />
                <span>
                  <strong>Platform-routed</strong> <em className="csri-recommended">Recommended</em>
                  <span className="csri-routing-detail">Goes to ProFenz's routing queue first, then is assigned to a servicing company. Matches how requests will flow once more than one company is on the platform.</span>
                </span>
              </label>
              <label className="csri-routing-option">
                <input type="radio" name="routing-mode" value="direct" aria-label="Direct to Carolina Server" checked={routingMode === 'direct'} onChange={() => setRoutingMode('direct')} />
                <span>
                  <strong>Direct to Carolina Server</strong>
                  <span className="csri-routing-detail">Skips the routing queue and creates the Company File immediately. Only meaningful while Carolina Server is the sole servicing company in the pilot.</span>
                </span>
              </label>
            </fieldset>}
            {!draftConnection && submitError && (
              <p className="csri-field-error" role="alert">
                {submitError}
              </p>
            )}
            <label className="csri-checkbox" htmlFor={reviewConfirmId}>
              <input
                id={reviewConfirmId}
                type="checkbox"
                checked={reviewConfirmed}
                aria-invalid={errors.review_confirmed ? 'true' : undefined}
                aria-describedby={errors.review_confirmed ? 'csri-review-confirmed-error' : undefined}
                onChange={(event) => {
                  setReviewConfirmed(event.target.checked);
                  setErrors((current) => {
                    const next = {...current};
                    delete next.review_confirmed;
                    return next;
                  });
                }}
              />
              I confirm the details above are accurate
            </label>
            {errors.review_confirmed && (
              <span id="csri-review-confirmed-error" className="csri-field-error">
                {errors.review_confirmed}
              </span>
            )}
          </fieldset>
        )}

        <div className="csri-actions">
          {stepIndex > 0 && (
            <button type="button" className="csri-secondary" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy} onClick={goBack}>
              Back
            </button>
          )}
          {step !== 'Review' ? (
            <button key="continue" type="button" className="csri-primary" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy} onClick={goNext}>
              Continue
            </button>
          ) : (
            <button key="save" type="submit" className="csri-primary" disabled={isSubmitting || attachingPapers || draftBlocked || draftConnection?.actionBusy}>
              {draftConnection ? (isSubmitting ? 'Saving…' : 'Save draft') : (isSubmitting ? 'Submitting…' : 'Submit request')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
